"use client";

import GlobalSearch from "./global-search/GlobalSearch";
import LanguageCombobox from "./LanguageCombobox";
import Image from "next/image";
import mainLogo from "@/assets/icons/sidebar/main-logo.svg";
import tovtripLogo from "@assets/icons/sidebar/tovtrip-logo.svg";
import IntegrationPartnerModal from "@/components/home/hero/integration-partner/IntegrationPartnerModal";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SiteFrame } from "@/components/layout/SiteFrame";
import { useUser } from "@/hooks/user/useUser";
import { Link } from "@/i18n/navigation";
import { cn } from "@/lib/utils";
import { AccountMenu } from "./AccountMenu";
import { AuthAction } from "./AuthAction";
import { IconDeviceTabletDown } from "@tabler/icons-react";
import { Typography } from "@/components/Typography";
import { useHeroIntegrationPartner } from "@/components/home/hero/integration-partner/useHeroIntegrationPartner";
import {
  headerNavItems,
  isIntegrationMainServiceItem,
  isInternalMainServiceItem,
  type MainServiceItemData,
} from "@/components/home/hero/mainServiceItems";
import { useServiceComingSoon } from "@/hooks/config/useWebConfig";
import { Button } from "@/components/ui/button";
import { useSetAtom } from "jotai";
import { dialogAtom } from "@/stores/dialogAtom";
import { bindScrollThreshold } from "@/lib/scroll";

type AppHeaderProps = {
  layout: "default-transparent" | "default-active" | "minimal";
  gSearch: boolean;
  showShadow?: boolean;
};

const SCROLL_THRESHOLD = 50;

const AppHeader = ({
  layout,
  gSearch = false,
  showShadow = true,
}: AppHeaderProps) => {
  const setDialog = useSetAtom(dialogAtom);
  const user = useUser();
  const headerRef = useRef<HTMLElement | null>(null);
  const items = useMemo(() => headerNavItems, []);
  const isMinimal = layout === "minimal";
  const isForcedActive = layout === "default-active";
  const [isScrolled, setIsScrolled] = useState(isForcedActive);
  const { isServiceComingSoon } = useServiceComingSoon();

  useEffect(() => {
    const el = headerRef.current;
    if (!el || isForcedActive) return;

    return bindScrollThreshold(
      (scrolled) => {
        el.toggleAttribute("data-scrolled", scrolled);
        setIsScrolled((prev) => (prev === scrolled ? prev : scrolled));
      },
      { threshold: SCROLL_THRESHOLD },
    );
  }, [isForcedActive]);

  const {
    activeIntegrationKey,
    closePartnerModal,
    isOpeningIntegrationLink,
    startPartnerRedirectFlow,
    partnerDialogState,
    retryPartnerRedirectFlow,
  } = useHeroIntegrationPartner();

  const handleServiceClick = useCallback(
    (item: MainServiceItemData) => {
      if (isServiceComingSoon(item)) {
        setDialog({
          id: "coming_soon_dialog",
          props: { type: item.key },
        });
        return;
      }

      if (
        isIntegrationMainServiceItem(item) &&
        !isOpeningIntegrationLink(item.integrationKey)
      ) {
        void startPartnerRedirectFlow(item.integrationKey);
      }
    },
    [
      isOpeningIntegrationLink,
      isServiceComingSoon,
      setDialog,
      startPartnerRedirectFlow,
    ],
  );

  const textColorClass = "text-white group-data-[scrolled]:text-heading";
  const btnStyle =
    "transition-all duration-300 ease-out hover:backdrop-blur-sm [@media(hover:hover)]:hover:bg-black/10 rounded-full";

  return (
    <>
      <header
        {...(isForcedActive ? { "data-scrolled": true } : {})}
        ref={headerRef}
        className={cn(
          "group fixed top-0 left-0 right-0 z-30 hidden w-full transition-all duration-500 ease-in-out md:block py-4 xl:px-0 px-2 items-center",
          "bg-transparent shadow-none backdrop-blur-0",
          showShadow
            ? "[&[data-scrolled]]:bg-white/95 [&[data-scrolled]]:shadow-sticky-bar [&[data-scrolled]]:backdrop-blur-sm"
            : "[&[data-scrolled]]:bg-white/95 [&[data-scrolled]]:backdrop-blur-sm",
          isMinimal ? "h-[72px]" : "h-auto",
        )}
      >
        <SiteFrame
          outerClassName="flex h-full items-center"
          innerClassName={cn(
            "lg:gap-[15px]",
            isMinimal ? "h-[48px]" : "h-auto",
          )}
        >
          {/* Top container  */}
          <section className="flex min-w-0 items-center gap-3">
            {isMinimal ? (
              <Link
                href="/"
                className="flex shrink-0 items-center"
                aria-label="Home"
              >
                <Image
                  src={mainLogo}
                  alt="Tovtrip"
                  width={48}
                  height={48}
                  className="h-12 w-12"
                  priority
                />
              </Link>
            ) : null}

            {!isMinimal ? (
              <Link href="/">
                <Image
                  src={tovtripLogo}
                  alt="Tovtrip"
                  width={400}
                  height={400}
                  className="h-auto w-auto"
                  priority
                />
              </Link>
            ) : null}

            {gSearch && <GlobalSearch isScrolled={isScrolled} />}

            <section className="ml-auto flex shrink-0 items-center gap-5">
              {layout !== "minimal" && (
                <div
                  className={cn(
                    "flex flex-cols items-center gap-2 cursor-pointer p-2",
                    textColorClass,
                    btnStyle,
                  )}
                  onClick={() => setDialog({ id: "download_app_dialog" })}
                >
                  <IconDeviceTabletDown stroke={2} />
                  <Typography variant="h6" className={textColorClass}>
                    App
                  </Typography>
                </div>
              )}

              <LanguageCombobox />

              {user.isLoading ? (
                <div className="flex h-8 w-8 items-center justify-center">
                  <div className="h-8 w-8 animate-pulse rounded-full bg-current opacity-20" />
                </div>
              ) : user.data ? (
                <AccountMenu />
              ) : (
                <AuthAction textColorClass={textColorClass} />
              )}
            </section>
          </section>

          {/* Menu section  */}
          {!isMinimal && (
            <section
              className={cn("min-w-0 flex-1", !gSearch ? "mt-0" : "mt-4")}
            >
              <div className="flex items-center overflow-x-auto no-scrollbar">
                {items.map((item) => {
                  const isComingSoon = isServiceComingSoon(item);
                  const isIntegration = isIntegrationMainServiceItem(item);
                  const isInternal = isInternalMainServiceItem(item);

                  const content = (
                    <div className="flex flex-col items-center gap-0! text-center shrink-0 min-w-[max-content] overflow-hidden">
                      <Button
                        asChild
                        variant={"outline"}
                        className={cn("bg-transparent border-none", btnStyle)}
                      >
                        <span>
                          <Typography
                            variant="h6"
                            className={cn(textColorClass, "")}
                          >
                            {item.label}
                          </Typography>
                        </span>
                      </Button>
                    </div>
                  );

                  if (isComingSoon) {
                    return (
                      <button
                        key={item.key}
                        type="button"
                        className="flex flex-col items-center"
                        onClick={() => handleServiceClick(item)}
                      >
                        {content}
                      </button>
                    );
                  }

                  if (isIntegration) {
                    return (
                      <button
                        key={item.key}
                        type="button"
                        className="flex flex-col items-center"
                        disabled={isOpeningIntegrationLink(item.integrationKey)}
                        onClick={() => handleServiceClick(item)}
                      >
                        {content}
                      </button>
                    );
                  }

                  if (isInternal) {
                    return (
                      <Link
                        key={item.key}
                        href={item.href}
                        className="flex flex-col items-center"
                      >
                        {content}
                      </Link>
                    );
                  }

                  return (
                    <button
                      key={item.key}
                      type="button"
                      className="flex flex-col items-center"
                    >
                      {content}
                    </button>
                  );
                })}
              </div>
            </section>
          )}
        </SiteFrame>
      </header>

      <IntegrationPartnerModal
        open={partnerDialogState !== "closed"}
        integrationKey={activeIntegrationKey}
        state={partnerDialogState === "error" ? "error" : "loading"}
        onClose={closePartnerModal}
        onRetry={retryPartnerRedirectFlow}
      />
    </>
  );
};

export default AppHeader;
